import {
    DetailsList, DetailsRow,
    IColumn, IDetailsListProps, IDetailsRowStyles,
    IDropdownOption,
    IObjectWithKey,
    Selection,
    SelectionMode,
    Stack,
    StackItem, TooltipOverflowMode
} from "@fluentui/react";
import {RecipientAndRequestDto} from "../../../model/RecipientAndRequestDto";
import Pagination from "../../Pagination/Pagination";
import React, {useEffect, useState} from "react";
import {TooltipHost} from "@fluentui/react/lib/Tooltip";

interface NcasDetailsListWithPaginationProps {
    items: RecipientAndRequestDto[];
    selectedItems: RecipientAndRequestDto[];
    onSelectedItemsChange: (selectedItems: RecipientAndRequestDto[]) => void;
}

interface ViewRequestsState {
    currentPage: number,
    itemsPerPage: number,
    columns: IColumn[],
    sortedItems: SortedRecipients[],
    stateItems: SortedRecipients[],
    selectedItems: RecipientAndRequestDto[],
}

interface SortedRecipients {
    key: string,
    id: number,
    caCode: string,
    caCodeToDisplay: string,
    caName: string,
    reasonForRequest: string,
    requestInformation: string,
    caInternalAssessmentNumber: string,
    eUSectoralLegislatureBasisName: string,
    dateOfAssessment: string,
    dateOfAssessmentValue: number,
    isHidden?: boolean
}

function NcasDetailsListWithPagination(props: NcasDetailsListWithPaginationProps) {

    const columns: IColumn[] = [
        {
            key: 'caName',
            name: 'Assessing CA',
            fieldName: 'caName',
            minWidth: 100,
            maxWidth: 191,
            isResizable: true,
            onColumnClick: _onColumnClick,
            onRenderHeader: () => (
                <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={'Assessing CA'}>{'Assessing CA'}</TooltipHost>
            ),
        },
        {
            key: 'caCode',
            name: 'CA code',
            fieldName: 'caCodeToDisplay',
            minWidth: 100,
            maxWidth: 135,
            isResizable: true,
            onColumnClick: _onColumnClick
        },
        {
            key: 'caInternalAssessmentNumber',
            name: 'CA internal assessment number',
            fieldName: 'caInternalAssessmentNumber',
            minWidth: 100,
            maxWidth: 225,
            isResizable: true,
            onColumnClick: _onColumnClick
        },
        {
            key: 'eUSectoralLegislatureBasisName',
            name: 'EU sectoral legislative basis',
            fieldName: 'eUSectoralLegislatureBasisName',
            minWidth: 100,
            maxWidth: 228,
            isResizable: true,
            onColumnClick: _onColumnClick
        },
        {
            key: 'dateOfAssessment',
            name: 'Date of assessment',
            fieldName: 'dateOfAssessmentValue',
            data: 'number',
            minWidth: 100,
            maxWidth: 221,
            isResizable: true,
            isSorted: true,
            isSortedDescending: true,
            onColumnClick: _onColumnClick,
            onRender: (item: SortedRecipients) => {
                return <span>{item.dateOfAssessment}</span>
            }
        },
    ];

    const unSortedItemsWithKeys: SortedRecipients[] = props.items.map(item => {
        const [day, month, year] = item.dateOfAssessment.split('/').map(Number);
        const dateObject = new Date(year, month - 1, day);

        return {
            ...item,
            key: item.id.toString(),
            dateOfAssessmentValue: dateObject.valueOf(),
        };
    });


    const itemsWithKeys = [...unSortedItemsWithKeys].sort((a, b) => b.dateOfAssessmentValue - a.dateOfAssessmentValue);

    const [userSelectedItems, setUserSelectedItems] = useState<RecipientAndRequestDto[]>(props.selectedItems);
    const [allSortedItems, setAllSortedItems] = useState<SortedRecipients[]>(itemsWithKeys);
    const [sortedColumns, setSortedColumns] = useState<IColumn[]>(columns);
    const ITEMS_PER_PAGE: number = 10;
    const INIT_PAGE: number = 1;
    const [modalState, setModalState] = React.useState<ViewRequestsState>({
        currentPage: INIT_PAGE,
        itemsPerPage: ITEMS_PER_PAGE,
        columns: columns,
        sortedItems: getNextNcaPage(ITEMS_PER_PAGE, INIT_PAGE),
        stateItems: getNextNcaPage(ITEMS_PER_PAGE, INIT_PAGE),
        selectedItems: props.selectedItems,
    });

    const [_selection, setSelection] = useState<Selection<IObjectWithKey>>(() => {
        return new Selection<IObjectWithKey>({
            onSelectionChanged: () => {
                const newSelectedItems = _selection.getSelection() as RecipientAndRequestDto[];
                console.log('onSelectionChanged', newSelectedItems);
                setUserSelectedItems(newSelectedItems);
            },
            getKey: _getKey,
            selectionMode: SelectionMode.multiple,
            onItemsChanged: () => {
            }
        });
    });

    function _getKey(item: any, index?: number): string {
        return item.key;
    }

    useEffect(() => {
        const selectedIds = modalState.selectedItems.map(item => item.id);
        modalState.sortedItems.forEach(item => {
            if (selectedIds.includes(item.id)) {
                _selection.setKeySelected(item.key, true, false);
            } else
            {
                _selection.setKeySelected(item.key, false, false);
            }
        });
        if(selectedIds.length === itemsWithKeys.length) {
            _selection.setAllSelected(true);
        }
    }, [modalState.sortedItems]);

    useEffect(() => {
        setModalState({
            ...modalState,
            selectedItems: userSelectedItems
        })
        props.onSelectedItemsChange(userSelectedItems);
    }, [userSelectedItems]);

    useEffect(() => {
        let newSortItems = getNextNcaPage(modalState.itemsPerPage, INIT_PAGE, allSortedItems);
        setModalState({
            ...modalState,
            currentPage: INIT_PAGE,
            sortedItems: newSortItems,
            columns: sortedColumns,
        });
    }, [allSortedItems, sortedColumns]);

    useEffect(() => {
        setModalState({
            ...modalState,
            sortedItems: modalState.stateItems
        });
    }, [modalState.stateItems]);

    function _copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
        const key = columnKey as keyof T;
        return items.slice(0).sort((a: T, b: T) => ((isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1));
    }

    function _onPageChange(selectedItem: number) {
        const nextPage = getNextNcaPage(modalState.itemsPerPage, selectedItem, modalState.sortedItems);
        setModalState({
            ...modalState,
            stateItems: nextPage,
            currentPage: selectedItem,
        });
    }

    function _onChangeItemsPerPage(event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) {
        setModalState({
            ...modalState,
            sortedItems: getNextNcaPage(item.key as number, INIT_PAGE, modalState.sortedItems),
            itemsPerPage: item.key as number,
            currentPage: 1,
        });
    }

    function getNextNcaPage(itemsPerPage: number, page: number, itemsSorted: SortedRecipients[] = allSortedItems) {
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        if (!itemsSorted.length) {
            return [];
        }
        return itemsSorted.map((item, index) => {
            if(index < startIndex || index >= endIndex) {
                return {
                    ...item,
                    isHidden: true
                }
            }
            return {
                ...item,
                isHidden: false
            }
        });
    }

    const _onRenderRow: IDetailsListProps['onRenderRow'] = props => {
        const customStyles: Partial<IDetailsRowStyles> = {};
        if (props) {
            // Find the item by key from stateItems
            const item = modalState.sortedItems.find(i => i.key === props.item.key);
            if (item?.isHidden) {
                // Every other row renders with a different background color
                customStyles.root = { display: 'none' };
                customStyles.cell = { display: 'none' };
                return null;
            }
            return <DetailsRow {...props} styles={customStyles} />;
        }
        return null;
    };

    function _onColumnClick(event: React.MouseEvent<HTMLElement>, column: IColumn) {
        const {columns} = modalState;
        let isSortedDescending = column.isSortedDescending;

        if (column.isSorted) {
            isSortedDescending = !isSortedDescending;
        }

        let allSortedItems = _copyAndSort(itemsWithKeys, column.fieldName!, isSortedDescending);
        setAllSortedItems(allSortedItems);
        setSortedColumns(columns.map(col => {
            col.isSorted = col.key === column.key;

            if (col.isSorted) {
                col.isSortedDescending = isSortedDescending;
            }
            return col;
        }));
    }

    function _renderItemColumn(item?: any, index?: number, column?: IColumn) {
        if(item && column) {
            const fieldContent = item[column.fieldName as keyof any] as string;
            return <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={fieldContent}>{fieldContent}</TooltipHost>
        }
    }

    const addTooltipToColumnHeader = (cols: IColumn[]) => {
        cols.forEach(col => {
            col.onRenderHeader= (props) => {
                return <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={props?.column.name}>{props?.column.name}</TooltipHost>
            }
        });
        return cols;
    };

    return (
        <Stack>
            <StackItem>
                <DetailsList
                    selection={_selection}
                    layoutMode={1}
                    columns={addTooltipToColumnHeader(modalState.columns)}
                    items={modalState.sortedItems}
                    selectionPreservedOnEmptyClick={true}
                    setKey="key"
                    onRenderRow={_onRenderRow}
                    onRenderItemColumn={_renderItemColumn}
                />
            </StackItem>
            <StackItem>
                <Pagination
                    currentPage={modalState.currentPage}
                    onPageChange={_onPageChange}
                    itemsPerPage={modalState.itemsPerPage}
                    onChangeItemsPerPage={_onChangeItemsPerPage}
                    totalItems={props.items.length}
                />
            </StackItem>
        </Stack>
    );
}

export default NcasDetailsListWithPagination;